import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Boxes, Plus } from "lucide-react";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CellStatusBadge, SpecPhaseBadge } from "@/components/StatusBadges";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OneTimeTokenDialog } from "@/components/OneTimeTokenDialog";
import { SpecDialog } from "@/components/dialogs/SpecDialog";
import { AdminInviteDialog } from "@/components/AdminInviteDialog";
import { useCell, useProvisionAudit, useSpecs } from "@/api/queries/provisioning";
import type { AdminInviteIssue } from "@/types";
import { useIssueEnrollToken } from "@/api/mutations/provisioning";
import { relativeTime } from "@/lib/relativeTime";
import { enrollHint } from "./List";

// #343 UC-1/UC-8: Zelle — Bindung, Spezifikationen der Zelle, neues Enroll-Token (Re-Enroll), Audit.
export function CellDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cell = useCell(id);
  const specs = useSpecs({ cellId: id });
  const audit = useProvisionAudit({ cellId: id, limit: 30 });
  const issue = useIssueEnrollToken();
  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // #343 (UC-9): Einladung des Erst-Admins aus der Antwort des Anlegens — genau einmal sichtbar.
  const [invite, setInvite] = useState<AdminInviteIssue | null>(null);

  if (cell.isLoading) return <div className="max-w-6xl"><Card><CardContent className="p-6" data-testid="detail-loading">Lade…</CardContent></Card></div>;
  if (cell.isError || !cell.data) {
    return (
      <div className="max-w-6xl space-y-4">
        <BackLink to="/cells" label="Zellen" />
        <Card><CardContent className="p-6 text-danger" data-testid="detail-error">Zelle nicht gefunden.</CardContent></Card>
      </div>
    );
  }
  const c = cell.data;
  const rows = specs.data ?? [];

  return (
    <div className="max-w-6xl space-y-5">
      <DetailHeader
        backTo="/cells"
        backLabel="Zellen"
        icon={Boxes}
        title={c.name}
        badge={<CellStatusBadge status={c.status} />}
        sub={`${c.provider} · ${c.lastSeenAt ? `zuletzt gesehen ${relativeTime(c.lastSeenAt)}` : "noch nie gemeldet"}`}
        actions={<Button variant="outline" onClick={() => setTokenOpen(true)} data-testid="issue-enroll-token">Neues Enroll-Token</Button>}
      />
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <SectionCard title="Kunden dieser Zelle" description="Spezifikationen — der Agent der Zelle rollt sie aus" noPad action={<Button size="sm" onClick={() => setCreateOpen(true)} disabled={c.status !== "enrolled"} data-testid="create-spec-for-cell"><Plus className="h-4 w-4" /> Kunde</Button>}>
            {rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-text3" data-testid="cell-specs-empty">Keine Spezifikationen.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Stand</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow key={s.id} data-clickable="true" onClick={() => navigate(`/customer-specs/${s.id}`)}>
                      <TableCell className="font-medium">{s.customer}</TableCell>
                      <TableCell>{s.plan.toUpperCase()}</TableCell>
                      <TableCell className="mono text-xs">{s.imageTag}</TableCell>
                      <TableCell className="tnum">v{s.version}{s.lastStatus && s.lastStatus.portalVersion !== s.version ? ` (ausgerollt v${s.lastStatus.portalVersion})` : ""}</TableCell>
                      <TableCell><SpecPhaseBadge phase={s.lastStatus?.phase} removed={s.removed} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
          <SectionCard title="Verlauf" description="Auftraege und Statusmeldungen dieser Zelle" noPad>
            {(audit.data ?? []).length === 0 ? (
              <div className="p-6 text-center text-sm text-text3">Noch keine Eintraege.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Zeit</TableHead><TableHead>Aktion</TableHead><TableHead>Akteur</TableHead><TableHead>Details</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {(audit.data ?? []).map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-text3">{relativeTime(e.ts)}</TableCell>
                      <TableCell className="mono text-xs">{e.action}</TableCell>
                      <TableCell className="text-text2">{e.actor}</TableCell>
                      <TableCell className="mono text-[11px] text-text3">{JSON.stringify(e.details)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </div>
        <PropGrid rows={[
          ["Zellen-ID", <span className="mono text-xs">{c.id}</span>],
          ["Provider", c.provider],
          ["Status", <CellStatusBadge status={c.status} />],
          ["Enrollt", c.enrolledAt ? relativeTime(c.enrolledAt) : "—"],
          ["Zuletzt gesehen", c.lastSeenAt ? relativeTime(c.lastSeenAt) : "—"],
          ["Enroll-Token", c.hasOpenEnrollToken ? `offen bis ${c.enrollTokenExpiresAt?.slice(0, 16).replace("T", " ")}` : "keins offen"],
          ["Angelegt", relativeTime(c.createdAt)],
        ]} />
      </div>
      {tokenOpen && (
        <ConfirmDialog
          open={tokenOpen}
          onOpenChange={setTokenOpen}
          title="Neues Enroll-Token"
          description="Fuer das Erst-Enroll oder ein Re-Enroll nach Schluesselverlust. Die bestehende Bindung bleibt, bis der Agent sich mit dem neuen Token meldet. Meldet er sich mit NEUEN Schluesseln, werden die an den alten Schluessel versiegelten Geheimnisse aller Kunden dieser Zelle ungueltig (stale) und muessen erneuert werden."
          confirmText="Token ausstellen"
          isPending={issue.isPending}
          errorMessage={issue.isError ? issue.error.message : undefined}
          onConfirm={() => issue.mutate(c.id, { onSuccess: (r) => { setTokenOpen(false); setToken(r.enrollToken); } })}
        />
      )}
      {token && (
        <OneTimeTokenDialog open={!!token} onOpenChange={(o) => !o && setToken(null)} title={`Enroll-Token fuer ${c.name}`} description={<span>In der Zelle: <code className="font-mono text-xs">{enrollHint(c.name)} --reset</code></span>} label="Enroll-Token" token={token} />
      )}
      {createOpen && <SpecDialog open={createOpen} onOpenChange={setCreateOpen} presetCellId={c.id} onCreated={(created) => setInvite(created.adminInvite)} />}
      <AdminInviteDialog invite={invite} onClose={() => setInvite(null)} />
    </div>
  );
}
